import { useState } from "react";
import { sendContactForm } from "@/lib/sendContactForm";

function typeButtonClass(active) {
  return `cursor-pointer rounded-[10px] border-3 border-ink px-[18px] py-2.5 text-sm font-bold transition-colors ${
    active ? "bg-yellow text-ink" : "bg-white text-ink"
  }`;
}

export function ContactPage({ games }) {
  const {
    contactPageOpen,
    closeContactPage,
    contactSent,
    contactName,
    contactType,
    setContactType,
    submitContact,
  } = games;

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  if (!contactPageOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email, and message.");
      return;
    }

    setStatus("sending");

    const result = await sendContactForm({
      name: form.name.trim(),
      email: form.email.trim(),
      type: contactType,
      message: form.message.trim(),
      source: "contact-page",
    });

    if (result.ok) {
      submitContact({ name: form.name.trim() });
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } else {
      setError(result.error);
      setStatus("idle");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-cream p-12 md:p-[50px_24px]">
      <div className="mx-auto max-w-[640px]">
        <button
          type="button"
          onClick={closeContactPage}
          className="neo-btn mb-[34px] rounded-[10px] bg-white px-[18px] py-2.5 text-sm text-ink"
        >
          ← Back to site
        </button>

        {contactSent || status === "sent" ? (
          <>
            <p className="mb-3 text-[44px]">🤝</p>
            <h2 className="mb-3.5 font-display text-[34px] text-ink">
              Got it, {contactName || form.name || "friend"}.
            </h2>
            <p className="text-base leading-relaxed text-muted">
              Your message just landed in my inbox. I&apos;ll read it properly and reply soon.
              Thanks for actually following through.
            </p>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <h2 className="mb-3 font-display text-[38px] text-ink">Let&apos;s talk.</h2>
            <p className="mb-[34px] text-base leading-relaxed text-muted">
              Tell me what you&apos;re building, what you need, and how to reach you. This goes
              straight to my inbox.
            </p>

            <div className="flex flex-col gap-4">
              <input
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="neo-input bg-white"
                required
                disabled={status === "sending"}
              />
              <input
                type="email"
                placeholder="Your email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="neo-input bg-white"
                required
                disabled={status === "sending"}
              />

              <div>
                <p className="mb-2.5 font-display text-[13px] text-ink">
                  What are you looking for?
                </p>
                <div className="flex flex-wrap gap-2.5">
                  <button
                    type="button"
                    onClick={() => setContactType("fulltime")}
                    className={typeButtonClass(contactType === "fulltime")}
                    disabled={status === "sending"}
                  >
                    Full-time role
                  </button>
                  <button
                    type="button"
                    onClick={() => setContactType("contract")}
                    className={typeButtonClass(contactType === "contract")}
                    disabled={status === "sending"}
                  >
                    Contract / consulting
                  </button>
                  <button
                    type="button"
                    onClick={() => setContactType("hi")}
                    className={typeButtonClass(contactType === "hi")}
                    disabled={status === "sending"}
                  >
                    Just saying hi
                  </button>
                </div>
              </div>

              <textarea
                placeholder="What are you building? What do you need?"
                rows={6}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className="neo-input resize-none bg-white"
                required
                disabled={status === "sending"}
              />

              {error && (
                <p className="rounded-lg border-2 border-pink bg-pink/10 px-3 py-2 text-sm font-semibold text-pink">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="neo-btn rounded-[14px] bg-yellow px-[26px] py-4 text-base text-ink disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === "sending" ? "Sending…" : "Send it my way"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
